// MastiBazaar API client.
// All backend calls funnel through here. Token is read from sessionStorage
// for admin and localStorage for customers — both layered into outgoing
// Authorization: Bearer headers, with the customer token used when no admin
// token is present (POST /api/orders accepts either or none).
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "mb_admin_token";
const CUSTOMER_TOKEN_KEY = "mb_customer_token";

export const getToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

const getCustomerBearer = () => {
  try {
    return localStorage.getItem(CUSTOMER_TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

export const setToken = (token) => {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
};

export const clearToken = () => setToken("");

const buildHeaders = (auth = false, json = true) => {
  const h = {};
  if (json) h["Content-Type"] = "application/json";
  if (auth) {
    const t = getToken();
    if (t) h["Authorization"] = `Bearer ${t}`;
  }
  return h;
};

const handle = async (resp) => {
  if (resp.status === 204) return null;
  const text = await resp.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!resp.ok) {
    const detail =
      (data && data.detail) || (typeof data === "string" ? data : resp.statusText);
    const err = new Error(typeof detail === "string" ? detail : "Request failed");
    err.status = resp.status;
    err.detail = detail;
    throw err;
  }
  return data;
};

// Build a public absolute URL for images. The backend returns image fields as
// `/api/images/{path}`; we prefix REACT_APP_BACKEND_URL so <img src> works.
export const absoluteImage = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
    return url;
  }
  if (url.startsWith("/")) return `${BACKEND}${url}`;
  return url;
};

// ---------- Auth ----------
// Login helper — the admin popup is password-only, so we omit the email
// field and let the backend resolve identity against ADMIN_EMAIL server-side.
export const apiLogin = async (passwordOrEmail, maybePassword) => {
  // Back-compat: callers passing (email, password) still work; callers
  // passing just (password) hit the password-only flow.
  const payload =
    maybePassword === undefined
      ? { password: passwordOrEmail }
      : { email: passwordOrEmail, password: maybePassword };
  const resp = await fetch(`${API}/auth/login`, {
    method: "POST",
    headers: buildHeaders(false, true),
    body: JSON.stringify(payload),
  });
  return handle(resp);
};

export const apiMe = async () => {
  const resp = await fetch(`${API}/auth/me`, {
    headers: buildHeaders(true, false),
  });
  return handle(resp);
};

// ---------- Products ----------
const hydrateProduct = (p) => ({
  ...p,
  image: absoluteImage(p.image),
  colors: Array.isArray(p.colors) ? p.colors.map(absoluteImage) : [],
  // NEW additive fields — extra gallery images (URLs/data-urls become absolute)
  // and text-based colour names. Default to [] when missing so the storefront
  // keeps rendering older products unchanged.
  images: Array.isArray(p.images) ? p.images.map(absoluteImage) : [],
  color_names: Array.isArray(p.color_names) ? p.color_names : [],
  stock_by_size: p.stock_by_size || {},
  // Treat a missing flag as Active so pre-existing products stay visible.
  is_active: p.is_active === false ? false : true,
});

export const apiListProducts = async ({ includeInactive = false } = {}) => {
  const url = includeInactive
    ? `${API}/products?include_inactive=true`
    : `${API}/products`;
  const resp = await fetch(url, {
    headers: buildHeaders(includeInactive, false),
  });
  const data = await handle(resp);
  return Array.isArray(data) ? data.map(hydrateProduct) : [];
};

export const apiCreateProduct = async (payload) => {
  const resp = await fetch(`${API}/products`, {
    method: "POST",
    headers: buildHeaders(true, true),
    body: JSON.stringify(payload),
  });
  return hydrateProduct(await handle(resp));
};

export const apiDeleteProduct = async (id) => {
  const resp = await fetch(`${API}/products/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: buildHeaders(true, false),
  });
  return handle(resp);
};

export const apiUpdateStock = async (id, stockBySize) => {
  const resp = await fetch(`${API}/products/${encodeURIComponent(id)}/stock`, {
    method: "PATCH",
    headers: buildHeaders(true, true),
    body: JSON.stringify({ stock_by_size: stockBySize }),
  });
  return hydrateProduct(await handle(resp));
};

export const apiUpdateVisibility = async (id, isActive) => {
  const resp = await fetch(
    `${API}/products/${encodeURIComponent(id)}/visibility`,
    {
      method: "PATCH",
      headers: buildHeaders(true, true),
      body: JSON.stringify({ is_active: !!isActive }),
    },
  );
  return hydrateProduct(await handle(resp));
};

export const apiBulkVisibility = async ({ isActive, ids, category }) => {
  const body = { is_active: !!isActive };
  if (ids && ids.length) body.ids = ids;
  else if (category) body.category = category;
  const resp = await fetch(`${API}/products/bulk-visibility`, {
    method: "PATCH",
    headers: buildHeaders(true, true),
    body: JSON.stringify(body),
  });
  return handle(resp);
};

export const apiUpdateSchedule = async (id, { activeFrom, activeUntil }) => {
  const resp = await fetch(
    `${API}/products/${encodeURIComponent(id)}/schedule`,
    {
      method: "PATCH",
      headers: buildHeaders(true, true),
      body: JSON.stringify({
        active_from: activeFrom || null,
        active_until: activeUntil || null,
      }),
    },
  );
  return hydrateProduct(await handle(resp));
};

// ---------- Direct selling-price + folder swap (admin quick-edits) ----------
export const apiUpdateProductPrice = async (id, price) => {
  const resp = await fetch(
    `${API}/products/${encodeURIComponent(id)}/price`,
    {
      method: "PATCH",
      headers: buildHeaders(true, true),
      body: JSON.stringify({ price: Number(price) || 0 }),
    },
  );
  return hydrateProduct(await handle(resp));
};

export const apiUpdateProductCategory = async (id, category) => {
  const resp = await fetch(
    `${API}/products/${encodeURIComponent(id)}/category`,
    {
      method: "PATCH",
      headers: buildHeaders(true, true),
      body: JSON.stringify({ category: String(category || "").trim() }),
    },
  );
  return hydrateProduct(await handle(resp));
};

// ---------- Categories (folders) ----------
export const apiListCategories = async ({ includeInactive = false } = {}) => {
  const url = includeInactive
    ? `${API}/categories?include_inactive=true`
    : `${API}/categories`;
  const resp = await fetch(url, {
    headers: buildHeaders(includeInactive, false),
  });
  const data = await handle(resp);
  return Array.isArray(data?.items) ? data.items : [];
};

export const apiUpdateCategoryVisibility = async (id, isActive) => {
  const resp = await fetch(
    `${API}/categories/${encodeURIComponent(id)}/visibility`,
    {
      method: "PATCH",
      headers: buildHeaders(true, true),
      body: JSON.stringify({ is_active: !!isActive }),
    },
  );
  return handle(resp);
};

// ---------- Returns ----------
export const apiCreateReturn = async (payload) => {
  const resp = await fetch(`${API}/returns`, {
    method: "POST",
    headers: buildHeaders(false, true),
    body: JSON.stringify(payload),
  });
  return handle(resp);
};

export const apiListReturns = async () => {
  const resp = await fetch(`${API}/returns`, {
    headers: buildHeaders(true, false),
  });
  return handle(resp);
};

export const apiListMyReturns = async () => {
  const ct = getCustomerBearer();
  if (!ct) return [];
  const resp = await fetch(`${API}/returns/mine`, {
    headers: { Authorization: `Bearer ${ct}` },
  });
  return handle(resp);
};

export const apiUpdateReturnStatus = async (id, status) => {
  const resp = await fetch(
    `${API}/returns/${encodeURIComponent(id)}/status`,
    {
      method: "PATCH",
      headers: buildHeaders(true, true),
      body: JSON.stringify({ status }),
    },
  );
  return handle(resp);
};

// ---------- Orders ----------
export const apiCreateOrder = async (payload) => {
  // If a customer is signed in, attach their bearer so the backend can link
  // the order to their account (powers /api/orders/mine).
  const headers = buildHeaders(false, true);
  const ct = getCustomerBearer();
  if (ct) headers["Authorization"] = `Bearer ${ct}`;
  const resp = await fetch(`${API}/orders`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  return handle(resp);
};

export const apiListMyOrders = async () => {
  const ct = getCustomerBearer();
  if (!ct) return [];
  const resp = await fetch(`${API}/orders/mine`, {
    headers: { Authorization: `Bearer ${ct}` },
  });
  return handle(resp);
};

export const apiListOrders = async () => {
  const resp = await fetch(`${API}/orders`, {
    headers: buildHeaders(true, false),
  });
  return handle(resp);
};

export const apiUpdateOrderStatus = async (orderId, status, extras = {}) => {
  const resp = await fetch(
    `${API}/orders/${encodeURIComponent(orderId)}/status`,
    {
      method: "PATCH",
      headers: buildHeaders(true, true),
      body: JSON.stringify({ status, ...extras }),
    },
  );
  return handle(resp);
};

export const apiDeleteOrder = async (orderId) => {
  const resp = await fetch(`${API}/orders/${encodeURIComponent(orderId)}`, {
    method: "DELETE",
    headers: buildHeaders(true, false),
  });
  return handle(resp);
};

// ---------- Branding ----------
export const apiGetBranding = async () => {
  const resp = await fetch(`${API}/settings/branding`);
  return handle(resp);
};

export const apiSetBranding = async (logoUrl) => {
  const resp = await fetch(`${API}/settings/branding`, {
    method: "PUT",
    headers: buildHeaders(true, true),
    body: JSON.stringify({ logo_url: logoUrl || "" }),
  });
  return handle(resp);
};

export const apiListNotifications = async (orderId) => {
  const qs = orderId ? `?order_id=${encodeURIComponent(orderId)}` : "";
  const resp = await fetch(`${API}/notifications${qs}`, {
    headers: buildHeaders(true, false),
  });
  return handle(resp);
};

// ---------- Reviews ----------
export const apiListReviews = async (productId) => {
  const qs = productId ? `?product_id=${encodeURIComponent(productId)}` : "";
  const resp = await fetch(`${API}/reviews${qs}`, {
    headers: buildHeaders(false, false),
  });
  return handle(resp);
};

export const apiCreateReview = async (payload) => {
  const resp = await fetch(`${API}/reviews`, {
    method: "POST",
    headers: buildHeaders(false, true),
    body: JSON.stringify(payload),
  });
  return handle(resp);
};

// ---------- Banners ----------
const hydrateBanner = (b) => ({ ...b, image: absoluteImage(b.image) });

export const apiListBanners = async () => {
  const resp = await fetch(`${API}/banners`, {
    headers: buildHeaders(false, false),
  });
  const data = await handle(resp);
  return Array.isArray(data) ? data.map(hydrateBanner) : [];
};

export const apiUpsertBanner = async (category, image) => {
  const resp = await fetch(`${API}/banners`, {
    method: "POST",
    headers: buildHeaders(true, true),
    body: JSON.stringify({ category, image }),
  });
  return hydrateBanner(await handle(resp));
};

export const apiDeleteBanner = async (category) => {
  const resp = await fetch(`${API}/banners/${encodeURIComponent(category)}`, {
    method: "DELETE",
    headers: buildHeaders(true, false),
  });
  return handle(resp);
};

// ---------- Settings ----------
export const apiGetSiteLive = async () => {
  const resp = await fetch(`${API}/settings/site_live`, {
    headers: buildHeaders(false, false),
  });
  const data = await handle(resp);
  return !!(data && data.live);
};

export const apiSetSiteLive = async (live) => {
  const resp = await fetch(`${API}/settings/site_live`, {
    method: "PUT",
    headers: buildHeaders(true, true),
    body: JSON.stringify({ live: !!live }),
  });
  return handle(resp);
};

// ---------- Upload ----------
// Accepts a dataURL or a plain http(s) URL.
// - dataURL → uploads to backend, returns absolute image URL.
// - http(s) URL → passes through.
export const apiUploadIfDataUrl = async (value) => {
  if (!value) return "";
  const v = String(value).trim();
  if (!v.startsWith("data:")) return v;
  const resp = await fetch(`${API}/upload/image`, {
    method: "POST",
    headers: buildHeaders(true, true),
    body: JSON.stringify({ data_url: v }),
  });
  const data = await handle(resp);
  return absoluteImage(data?.url || "");
};

// ---------- Migration ----------
export const apiMigrate = async (payload) => {
  const resp = await fetch(`${API}/migrate`, {
    method: "POST",
    headers: buildHeaders(true, true),
    body: JSON.stringify(payload),
  });
  return handle(resp);
};

// ---------- Notify (mocked, kept for back-compat) ----------
export const apiNotify = async (payload) => {
  try {
    await fetch(`${API}/notify`, {
      method: "POST",
      headers: buildHeaders(false, true),
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    /* mocked */
  }
};
